import React, { useState, useEffect, useCallback } from 'react';
import { Image as ImageIcon, Upload, Globe, EyeOff, Edit, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function EditProjectForm({ auth, project, onBack, onSuccess }) {
  const [formData, setFormData] = useState({
    gravestone_name: project.gravestone_name || '',
    cemetery: project.cemetery || '',
    municipality: project.municipality || '',
    county: project.county || '',
    work_date: project.work_date || '',
    notes: project.notes || '',
    project_type: project.project_type || 'gravstein',
    consent_website: project.consent_website || false,
    consent_social_media: project.consent_social_media || false,
    published: project.published || false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');
    try {
      const res = await fetch(`${API}/admin/projects/${project.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        setMessage('Prosjekt oppdatert!');
        setTimeout(() => onSuccess(), 800);
      } else {
        const err = await res.json();
        setMessage(err.detail || 'Kunne ikke oppdatere prosjektet.');
      }
    } catch { setMessage('Kunne ikke koble til serveren.'); }
    finally { setIsSubmitting(false); }
  };

  const beforeImg = (project.images || []).find(i => i.category === 'before');
  const afterImg = (project.images || []).find(i => i.category === 'after');

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-2 text-stone-600 hover:text-stone-900 mb-6 py-2" data-testid="back-from-edit">
        <ArrowLeft size={16} /> Tilbake til galleri
      </button>
      <h2 className="text-xl sm:text-2xl font-semibold text-stone-900 mb-4">Rediger prosjekt</h2>

      <div className="grid grid-cols-2 gap-2 mb-6 rounded-xl overflow-hidden">
        {beforeImg && (
          <div className="relative">
            <img src={beforeImg.image_url} alt="For" className="w-full h-40 object-cover" />
            <span className="absolute bottom-2 left-2 bg-stone-900/70 text-white text-xs px-2 py-1 rounded">FOR</span>
          </div>
        )}
        {afterImg && (
          <div className="relative">
            <img src={afterImg.image_url} alt="Etter" className="w-full h-40 object-cover" />
            <span className="absolute bottom-2 right-2 bg-emerald-800/70 text-white text-xs px-2 py-1 rounded">ETTER</span>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-stone-200 p-4 sm:p-8 space-y-5" data-testid="edit-project-form">
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">Prosjekttype</label>
          <div className="flex gap-3">
            <button type="button" onClick={() => setFormData({...formData, project_type: 'gravstein'})}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${formData.project_type === 'gravstein' ? 'bg-emerald-800 text-white' : 'bg-stone-100 text-stone-600'}`}>
              Gravstein
            </button>
            <button type="button" onClick={() => setFormData({...formData, project_type: 'monument'})}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${formData.project_type === 'monument' ? 'bg-amber-600 text-white' : 'bg-stone-100 text-stone-600'}`}>
              Monument
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">{formData.project_type === 'monument' ? 'Monumentets navn' : 'Gravsteinens navn'}</label>
            <input type="text" value={formData.gravestone_name} onChange={(e) => setFormData({...formData, gravestone_name: e.target.value})} className="w-full px-3 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-sm" placeholder="Valgfritt" data-testid="edit-name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Kirkegård</label>
            <input type="text" value={formData.cemetery} onChange={(e) => setFormData({...formData, cemetery: e.target.value})} className="w-full px-3 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-sm" placeholder="Valgfritt" data-testid="edit-cemetery-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Kommune</label>
            <input type="text" value={formData.municipality} onChange={(e) => setFormData({...formData, municipality: e.target.value})} className="w-full px-3 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-sm" placeholder="Valgfritt" data-testid="edit-municipality-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Dato</label>
            <input type="date" value={formData.work_date} onChange={(e) => setFormData({...formData, work_date: e.target.value})} className="w-full px-3 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-sm" data-testid="edit-date-input" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">Notat</label>
          <textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="w-full px-3 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-sm" rows={2} placeholder="Kort beskrivelse..." data-testid="edit-notes-input" />
        </div>
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={formData.consent_website} onChange={(e) => setFormData({...formData, consent_website: e.target.checked})} className="w-5 h-5 rounded border-stone-300 text-emerald-800 focus:ring-emerald-800" />
            <span className="text-sm text-stone-700">Vis på nettside</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={formData.published} onChange={(e) => setFormData({...formData, published: e.target.checked})} className="w-5 h-5 rounded border-stone-300 text-emerald-800 focus:ring-emerald-800" />
            <span className="text-sm text-stone-700">Publisert</span>
          </label>
        </div>
        {message && <p className={`text-center text-sm ${message.includes('oppdatert') ? 'text-emerald-600' : 'text-red-600'}`}>{message}</p>}
        <button type="submit" disabled={isSubmitting} className="w-full bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-6 py-3 font-medium transition-colors disabled:opacity-50" data-testid="save-edit-btn">
          {isSubmitting ? 'Lagrer...' : 'Lagre endringer'}
        </button>
      </form>
    </div>
  );
}

function UploadProjectForm({ auth, onBack, onSuccess }) {
  const [formData, setFormData] = useState({
    gravestone_name: '', cemetery: '', municipality: '', county: '', work_date: new Date().toISOString().slice(0, 10),
    notes: '', consent_website: false, consent_social_media: false, published: false, project_type: 'gravstein'
  });
  const [beforeImages, setBeforeImages] = useState([]);
  const [afterImages, setAfterImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState('');

  const compressImage = (file, maxWidth = 1600, quality = 0.7) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFiles = (files, type) => {
    const fileArray = Array.from(files).slice(0, 4);
    const promises = fileArray.map(file => compressImage(file));
    Promise.all(promises).then(results => {
      if (type === 'before') setBeforeImages(prev => [...prev, ...results].slice(0, 4));
      else setAfterImages(prev => [...prev, ...results].slice(0, 4));
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (beforeImages.length === 0 || afterImages.length === 0) {
      setMessage('Du må laste opp minst 1 for-bilde og 1 etter-bilde');
      return;
    }
    setIsSubmitting(true);
    setMessage('');
    setUploadProgress('Laster opp bilder...');
    try {
      const res = await fetch(`${API}/admin/projects`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, before_images: beforeImages, after_images: afterImages })
      });
      if (res.ok) {
        setUploadProgress('');
        setMessage('Prosjekt lastet opp!');
        setTimeout(() => onSuccess(), 1000);
      } else {
        const err = await res.json().catch(() => ({ detail: `Serverfeil (${res.status})` }));
        setMessage(typeof err.detail === 'string' ? err.detail : 'Opplasting feilet. Sjekk bildene og prøv igjen.');
        setUploadProgress('');
      }
    } catch (e) { setMessage('Kunne ikke koble til serveren: ' + e.message); setUploadProgress(''); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-2 text-stone-600 hover:text-stone-900 mb-6" data-testid="back-from-upload">
        <ArrowLeft size={16} /> Tilbake til galleri
      </button>
      <h2 className="text-2xl font-semibold text-stone-900 mb-6">Last opp nytt prosjekt</h2>
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-stone-200 p-8 space-y-6" data-testid="upload-project-form">
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">Prosjekttype</label>
          <div className="flex gap-3">
            <button type="button" onClick={() => setFormData({...formData, project_type: 'gravstein'})}
              className={`flex-1 py-3 rounded-lg text-sm font-medium transition-all ${formData.project_type === 'gravstein' ? 'bg-emerald-800 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
              data-testid="project-type-gravstein">
              Gravstein
            </button>
            <button type="button" onClick={() => setFormData({...formData, project_type: 'monument'})}
              className={`flex-1 py-3 rounded-lg text-sm font-medium transition-all ${formData.project_type === 'monument' ? 'bg-amber-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
              data-testid="project-type-monument">
              Monument
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">{formData.project_type === 'monument' ? 'Monumentets navn' : 'Gravsteinens navn'}</label>
            <input type="text" value={formData.gravestone_name} onChange={(e) => setFormData({...formData, gravestone_name: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent" placeholder="Valgfritt - kan legges til senere" data-testid="project-name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Kirkegård</label>
            <input type="text" value={formData.cemetery} onChange={(e) => setFormData({...formData, cemetery: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent" placeholder="Valgfritt - kan legges til senere" data-testid="project-cemetery-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Kommune</label>
            <input type="text" value={formData.municipality} onChange={(e) => setFormData({...formData, municipality: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent" placeholder="Valgfritt - kan legges til senere" data-testid="project-municipality-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Dato for arbeid</label>
            <input type="date" value={formData.work_date} onChange={(e) => setFormData({...formData, work_date: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent" data-testid="project-date-input" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">Notat</label>
          <textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent" rows={2} placeholder="Beskrivelse av arbeidet..." />
        </div>

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">For-bilder (maks 4) *</label>
          <div className="flex gap-3 flex-wrap">
            {beforeImages.map((img, i) => (
              <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-stone-300">
                <img src={img} alt={`For ${i+1}`} className="w-full h-full object-cover" />
                <button type="button" onClick={() => setBeforeImages(prev => prev.filter((_, idx) => idx !== i))} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">x</button>
                <span className="absolute bottom-0.5 left-0.5 bg-stone-900/70 text-white text-xs px-1 rounded">FOR</span>
              </div>
            ))}
            {beforeImages.length < 4 && (
              <label className="w-24 h-24 border-2 border-dashed border-stone-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-stone-400 text-stone-400">
                <Upload size={20} />
                <span className="text-xs mt-1">Legg til</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files, 'before')} data-testid="before-image-input" />
              </label>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">Etter-bilder (maks 4) *</label>
          <div className="flex gap-3 flex-wrap">
            {afterImages.map((img, i) => (
              <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-emerald-300">
                <img src={img} alt={`Etter ${i+1}`} className="w-full h-full object-cover" />
                <button type="button" onClick={() => setAfterImages(prev => prev.filter((_, idx) => idx !== i))} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">x</button>
                <span className="absolute bottom-0.5 left-0.5 bg-emerald-800/70 text-white text-xs px-1 rounded">ETTER</span>
              </div>
            ))}
            {afterImages.length < 4 && (
              <label className="w-24 h-24 border-2 border-dashed border-emerald-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-emerald-400 text-emerald-400">
                <Upload size={20} />
                <span className="text-xs mt-1">Legg til</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files, 'after')} data-testid="after-image-input" />
              </label>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={formData.consent_website} onChange={(e) => setFormData({...formData, consent_website: e.target.checked})} className="w-5 h-5 rounded border-stone-300 text-emerald-800 focus:ring-emerald-800" data-testid="consent-website-checkbox" />
            <span className="text-sm text-stone-700">Samtykke til visning på nettside</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={formData.consent_social_media} onChange={(e) => setFormData({...formData, consent_social_media: e.target.checked})} className="w-5 h-5 rounded border-stone-300 text-emerald-800 focus:ring-emerald-800" />
            <span className="text-sm text-stone-700">Samtykke til bruk i sosiale medier</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={formData.published} onChange={(e) => setFormData({...formData, published: e.target.checked})} className="w-5 h-5 rounded border-stone-300 text-emerald-800 focus:ring-emerald-800" data-testid="publish-checkbox" />
            <span className="text-sm text-stone-700">Publiser med en gang</span>
          </label>
        </div>

        {uploadProgress && <p className="text-amber-600 text-center">{uploadProgress}</p>}
        {message && <p className={`text-center ${message.includes('lastet') ? 'text-emerald-600' : 'text-red-600'}`}>{message}</p>}

        <button type="submit" disabled={isSubmitting} className="w-full bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-6 py-3 font-medium transition-colors disabled:opacity-50" data-testid="submit-project-btn">
          {isSubmitting ? 'Laster opp...' : 'Last opp prosjekt'}
        </button>
      </form>
    </div>
  );
}

export default function GalleryTab({ auth }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  const loadProjects = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/projects`, { headers: { 'Authorization': `Basic ${auth}` } });
      if (res.ok) setProjects(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [auth]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const togglePublish = async (projectId, currentStatus) => {
    try {
      await fetch(`${API}/admin/projects/${projectId}/publish?published=${!currentStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Basic ${auth}` }
      });
      loadProjects();
    } catch { /* ignore */ }
  };

  if (showUpload) {
    return <UploadProjectForm auth={auth} onBack={() => setShowUpload(false)} onSuccess={() => { setShowUpload(false); loadProjects(); }} />;
  }

  if (editingProject) {
    return <EditProjectForm auth={auth} project={editingProject} onBack={() => setEditingProject(null)} onSuccess={() => { setEditingProject(null); loadProjects(); }} />;
  }

  if (loading) return <div className="text-stone-500">Laster...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-stone-900">Galleri ({projects.length} prosjekter)</h2>
        <button onClick={() => setShowUpload(true)} className="flex items-center gap-2 bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-4 py-2 text-sm font-medium transition-colors" data-testid="upload-project-btn">
          <Upload size={16} /> Last opp nytt prosjekt
        </button>
      </div>

      {projects.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-12 text-center">
          <ImageIcon className="mx-auto text-stone-300 mb-4" size={48} />
          <p className="text-stone-500">Ingen prosjekter ennå. Last opp ditt første!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-gallery-grid">
          {projects.map((p) => {
            const beforeImg = (p.images || []).find(i => i.category === 'before');
            const afterImg = (p.images || []).find(i => i.category === 'after');
            return (
              <div key={p.id} className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden" data-testid={`admin-project-${p.id}`}>
                <div className="grid grid-cols-2 gap-0.5">
                  {beforeImg ? (
                    <div className="relative">
                      <img src={beforeImg.image_url} alt="For" className="w-full h-32 object-cover" />
                      <span className="absolute bottom-1 left-1 bg-stone-900/70 text-white text-xs px-2 py-0.5 rounded">FOR</span>
                    </div>
                  ) : <div className="bg-stone-100 h-32 flex items-center justify-center text-stone-400 text-xs">Ingen bilde</div>}
                  {afterImg ? (
                    <div className="relative">
                      <img src={afterImg.image_url} alt="Etter" className="w-full h-32 object-cover" />
                      <span className="absolute bottom-1 right-1 bg-emerald-800/70 text-white text-xs px-2 py-0.5 rounded">ETTER</span>
                    </div>
                  ) : <div className="bg-stone-100 h-32 flex items-center justify-center text-stone-400 text-xs">Ingen bilde</div>}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-stone-900">{p.gravestone_name || 'Uten navn'}</h3>
                    {p.project_type === 'monument' && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Monument</span>}
                  </div>
                  <p className="text-sm text-stone-500">{[p.cemetery, p.municipality].filter(Boolean).join(', ') || 'Sted ikke oppgitt'}</p>
                  <p className="text-xs text-stone-400 mt-1">{p.work_date || 'Dato ikke satt'} | {(p.images || []).length} bilder</p>
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <button
                      onClick={() => togglePublish(p.id, p.published)}
                      className={`flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
                        p.published
                          ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                      data-testid={`toggle-publish-${p.id}`}
                    >
                      {p.published ? <><Globe size={12} /> Publisert</> : <><EyeOff size={12} /> Ikke publisert</>}
                    </button>
                    <button
                      onClick={() => setEditingProject(p)}
                      className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"
                      data-testid={`edit-project-${p.id}`}
                    >
                      <Edit size={12} /> Rediger
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
